<template>
  <b-nav-item-dropdown id="notifications-dropdown-header" no-caret right @shown="markRead">
    <template #button-content>
      <span class="bell" :title="title">
        <font-awesome-icon icon="bell" :class="bellClass"/>
        <b-badge v-if="count > 0" pill :variant="badgeVariant" class="bell-count">{{ count }}</b-badge>
      </span>
    </template>

    <b-dropdown-text class="job-dropdown-menu">
      <div class="d-flex align-items-center">
        <strong>Notifications</strong>
        <b-link v-if="doneJobs.length > 0" class="ml-auto small" @click.stop.prevent="clearAll">Clear all</b-link>
      </div>
    </b-dropdown-text>
    <b-dropdown-divider/>

    <b-dropdown-text v-if="jobs.length === 0 && problems.length === 0" class="text-secondary">No notifications</b-dropdown-text>

    <!-- The organization's problems, until they're fixed (OSA-105) -->
    <template v-if="problems.length > 0">
      <b-dropdown-text class="small text-uppercase text-secondary section-title">Needs attention</b-dropdown-text>
      <b-dropdown-text v-for="problem in problems" :key="problem.uuid" class="job-dropdown-item">
        <div class="d-flex align-items-start">
          <font-awesome-icon icon="exclamation-circle" fixed-width class="mr-2 mt-1 text-danger"/>
          <div class="flex-grow-1" :class="{'font-weight-bold': !problem.read}">
            <template v-if="problem.kind === 'runners-without-team'">
              <router-link v-if="canManageTeams" :to="{name: 'users-and-teams', query: {filter: 'true'}}">
                {{ runners(problem) }}
              </router-link>
              <template v-else>{{ runners(problem) }}</template>
              {{ have(problem) }} no team
            </template>
            <template v-else-if="problem.kind === 'garmin-access-lost'">
              {{ runners(problem) }} lost their Garmin access
            </template>
            <!-- On a team, so meant to get workouts, but never connected Garmin (OSA-129) -->
            <template v-else-if="problem.kind === 'garmin-not-connected'">
              {{ runners(problem) }} {{ have(problem) }}n't connected Garmin
            </template>
            <template v-else-if="problem.kind === 'program-sync-failed'">
              Couldn't sync the program workouts of {{ runners(problem) }}
            </template>
            <template v-else>{{ (problem.data && problem.data.message) || typeLabel(problem.kind) }}</template>
            <!-- Who: like clicking a runner's name on the Runners page -->
            <div v-if="namedRunners(problem).length > 0" class="small font-weight-normal text-secondary">
              <template v-for="(runner, index) in namedRunners(problem)">
                <span v-if="index > 0" :key="`${runner.uuid}-separator`">, </span>
                <router-link v-if="canManageTeams" :key="runner.uuid"
                             :to="{name: 'users-and-teams', query: {q: runner.name}}">{{ runner.name }}</router-link>
                <span v-else :key="runner.uuid">{{ runner.name }}</span>
              </template>
              <span v-if="moreRunners(problem) > 0"> and {{ moreRunners(problem) }} more</span>
            </div>
          </div>
          <b-link class="ml-2 text-secondary" title="Dismiss until it changes" @click.stop.prevent="$emit('dismiss-problem', problem)">
            <font-awesome-icon icon="times"/>
          </b-link>
        </div>
      </b-dropdown-text>
      <b-dropdown-divider v-if="jobs.length > 0"/>
    </template>

    <b-dropdown-text v-for="job in jobs" :key="job.uuid" class="job-dropdown-item">
      <div class="d-flex align-items-start">
        <font-awesome-icon :icon="iconOf(job)" :spin="!isDone(job)" fixed-width class="mr-2 mt-1" :class="stateClass(job)"/>
        <div class="flex-grow-1">
          <div>
            <template v-if="job.type === 'publish-workout' && job.data && job.data.workout">
              {{ verb(job, 'Publishing', 'Published', 'Publish') }}
              <router-link :to="{ name: 'workout', params: { uuid: job.data.workout.uuid }}">{{ job.data.workout.name }}</router-link>
              to {{ runners(job) }}
            </template>
            <template v-else-if="job.type === 'schedule-plan' && job.data && job.data.plan">
              {{ verb(job, 'Publishing', 'Published', 'Publish') }}
              <router-link :to="{ name: 'plan', params: { uuid: job.data.plan.uuid }}">{{ job.data.plan.name }}</router-link>
              to {{ runners(job) }}
            </template>
            <template v-else-if="job.type === 'workouts-sync'">
              {{ verb(job, 'Syncing', 'Synced', 'Sync') }} the workouts of {{ runners(job) }}
            </template>
            <template v-else-if="job.type === 'api-validate'">
              {{ verb(job, 'Checking', 'Checked', 'Check') }} the Garmin access of {{ runners(job) }}
            </template>
            <template v-else>{{ typeLabel(job.type) }}</template>
          </div>
          <div v-if="isDone(job)" class="small" :class="stateClass(job)">{{ stateText(job) }}</div>
          <b-progress v-else :value="job.progress * 100" max="100" height="3px" class="mt-1"/>
        </div>
        <!-- The server only lets finished jobs be dismissed -->
        <b-link v-if="isDone(job)" class="ml-2 text-secondary" title="Dismiss" @click.stop.prevent="$emit('dismiss', job)">
          <font-awesome-icon icon="times"/>
        </b-link>
      </div>
    </b-dropdown-text>
  </b-nav-item-dropdown>
</template>

<script>
/**
 * The current organization's problems (OSA-105) and the user's background jobs (OSA-102), in the navbar. Status names are the server's jobs.model.Status names.
 */
const DONE = ['Completed', 'Skipped', 'Cancelled', 'Aborted', 'TimedOut', 'Failed'];
const FAILED = ['Aborted', 'TimedOut', 'Failed'];
// What the job's tasks did, as the server counts them (OSA-107), in the order shown
const RESULTS = [
  ['published', count => `${count} new`],
  ['updated', count => `${count} updated`],
  ['up-to-date', count => `${count} already up to date`],
  ['connected', count => `${count} connected`],
  ['not-applicable', count => `${count} not matching the labels`],
  ['no-access', count => `${count} without Garmin access`],
  ['failed', count => `${count} failed`],
];
// Runners who didn't get it
const MISSED = ['no-access', 'failed'];
// A problem names this many of its runners, then says how many more
const MAX_NAMED_RUNNERS = 3;
const STATE_TEXT = {
  Completed: 'Done',
  Skipped: 'Skipped',
  Cancelled: 'Cancelled',
  Aborted: 'Stopped before it finished',
  TimedOut: 'Timed out',
  Failed: 'Failed',
};

export default {
  name: 'NotificationBell',
  props: {
    jobs: {
      type: Array,
      default: () => [],
    },
    // The current organization's open problems, from GET /api/notifications. Runners have none.
    problems: {
      type: Array,
      default: () => [],
    },
    // Whether the user may open Users and Teams, where runners get a team
    canManageTeams: Boolean,
  },
  computed: {
    doneJobs() {
      return this.jobs.filter(this.isDone);
    },
    unreadProblems() {
      return this.problems.filter(problem => !problem.read);
    },
    // Problems count until the user saw them; jobs until dismissed
    count() {
      return this.unreadProblems.length + this.jobs.length;
    },
    anyFailed() {
      return this.unreadProblems.length > 0 || this.jobs.some(this.isFailed);
    },
    anyRunning() {
      return this.jobs.some(job => !this.isDone(job));
    },
    bellClass() {
      return this.anyFailed ? 'text-danger' : this.anyRunning ? 'text-warning' : '';
    },
    badgeVariant() {
      return this.anyFailed ? 'danger' : this.anyRunning ? 'warning' : 'light';
    },
    title() {
      const count = this.jobs.length + this.problems.length;
      return count === 0 ? 'No notifications' : count === 1 ? '1 notification' : `${count} notifications`;
    },
  },
  methods: {
    markRead() {
      if (this.unreadProblems.length > 0) {
        this.$emit('read-problems', this.unreadProblems.map(problem => problem.uuid));
      }
    },
    // The first few of the problem's runners, by name (OSA-106)
    namedRunners(problem) {
      const runners = problem.data && Array.isArray(problem.data.runners) ? problem.data.runners : [];
      return runners.slice(0, MAX_NAMED_RUNNERS);
    },
    moreRunners(problem) {
      const runners = problem.data && Array.isArray(problem.data.runners) ? problem.data.runners : [];
      return Math.max(0, runners.length - MAX_NAMED_RUNNERS);
    },
    have(item) {
      return item.data && item.data.count === 1 ? 'has' : 'have';
    },
    isDone(job) {
      return DONE.includes(job.status);
    },
    isFailed(job) {
      return FAILED.includes(job.status);
    },
    results(job) {
      return job.status === 'Completed' && job.data && job.data.results ? job.data.results : null;
    },
    stateText(job) {
      const results = this.results(job);
      const parts = results ? RESULTS.filter(([key]) => results[key] > 0).map(([key, text]) => text(results[key])) : [];
      return parts.length > 0 ? parts.join(', ') : STATE_TEXT[job.status] || job.status;
    },
    // Done, but some runners didn't get it
    isPartial(job) {
      const results = this.results(job);
      return results !== null && MISSED.some(key => results[key] > 0);
    },
    stateClass(job) {
      if (this.isFailed(job)) {
        return 'text-danger';
      }
      if (this.isPartial(job)) {
        return 'text-warning';
      }
      return this.isDone(job) ? 'text-success' : 'text-secondary';
    },
    iconOf(job) {
      if (this.isFailed(job) || this.isPartial(job)) {
        return 'exclamation-circle';
      }
      return this.isDone(job) ? 'check-circle' : 'spinner';
    },
    /**
     * @return The verb for the job's state: running, succeeded, or not (failed, timed out...), as in "Publish … : Failed"
     */
    verb(job, running, succeeded, other) {
      if (!this.isDone(job)) {
        return running;
      }
      return job.status === 'Completed' ? succeeded : other;
    },
    runners(job) {
      const count = job.data && job.data.count !== undefined ? job.data.count : 0;
      return count === 1 ? '1 runner' : `${count} runners`;
    },
    typeLabel(type) {
      // e.g. 'purge-workouts' -> 'Purge workouts'
      const words = (type || 'job').replace(/[-_]/g, ' ');
      return words.charAt(0).toUpperCase() + words.slice(1);
    },
    clearAll() {
      this.doneJobs.forEach(job => this.$emit('dismiss', job));
    },
  },
}
</script>

<style scoped>
.bell {
  position: relative;
  display: inline-block;
}

.bell-count {
  position: absolute;
  top: -6px;
  right: -10px;
  font-size: 0.6rem;
}

.job-dropdown-menu {
  width: 400px;
}

.section-title {
  letter-spacing: 0.05em;
}

.job-dropdown-item {
  width: 100%;
}
</style>
